/**
 * Maximum take-off mass of an aircraft (`mtom_kg`, the source of the EU UA class C0–C4).
 *
 * POST /aircraft requires a positive value (400 aircraft_mtom_required). PUT /aircraft/{id} treats null as
 * "not sent" and rejects only a newly sent value <= 0 (aircraft_mtom_invalid), so an aircraft stored before the
 * MTOM became required — without one or with one <= 0 — stays editable; the form only warns, like the maximum
 * speed (lib/aircraftMaxSpeed.js).
 */
import { isMaxSpeedMissing } from './aircraftMaxSpeed';

/** The aircraft has no usable MTOM: its UA class is Unknown and SORA falls back to the conservative 25 kg. */
export const isMtomMissing = (aircraft) => !(Number(aircraft?.mtom_kg) > 0);

/** The form requires the MTOM on create and when the edited aircraft already has a usable one (it cannot be cleared). */
export const isMtomRequired = (editingAircraft) => !editingAircraft || !isMtomMissing(editingAircraft);

/**
 * Locale key (in `t.fleet`) of the MTOM field's helper text: required on create and for an aircraft with a usable
 * MTOM, only recommended for an aircraft stored without one.
 */
export const mtomHelperKey = (editingAircraft) =>
  isMtomRequired(editingAircraft) ? 'mtomHelper' : 'mtomHelperRecommended';

/**
 * Locale key (in `t.fleet`) of the form's MTOM error, or null when the value passes: empty where required →
 * mtomRequired, a filled value that is not a positive number → mtomPositive, except the unchanged stored value
 * <= 0 of an aircraft saved before the MTOM became required (the partial PUT does not send it).
 */
export const mtomValidationKey = (value, editingAircraft) => {
  const empty = value === undefined || value === null || String(value).trim() === '';
  if (empty) return isMtomRequired(editingAircraft) ? 'mtomRequired' : null;
  if (Number(value) > 0) return null;
  const unchangedStoredValue = editingAircraft?.mtom_kg != null
    && Number(value) === Number(editingAircraft.mtom_kg);
  return unchangedStoredValue ? null : 'mtomPositive';
};

/**
 * Locale keys (in `t.fleet`) of the one warning shown for an aircraft stored without a usable MTOM and/or speed —
 * the fleet list badge and the detail page — or null when both are present.
 */
export const missingSoraInputsKeys = (aircraft) => {
  const mtomMissing = isMtomMissing(aircraft);
  const speedMissing = isMaxSpeedMissing(aircraft);
  if (mtomMissing && speedMissing) return { label: 'soraInputsMissing', hint: 'soraInputsMissingHint' };
  if (mtomMissing) return { label: 'mtomMissing', hint: 'mtomMissingHint' };
  if (speedMissing) return { label: 'maxSpeedMissing', hint: 'maxSpeedMissingHint' };
  return null;
};
